import { RotateCcw, Trash2, Truck, FileText, Image as ImgIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { money, dateShort } from "@/lib/format";

const daysLeft = (iso) => Math.max(0, Math.ceil((new Date(iso) - Date.now()) / 86400000));
export const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

const Actions = ({ id, onRestore, onPurge, restoreDisabled, restoreHint }) => (
  <div className="flex items-center gap-2 shrink-0">
    <Button size="sm" variant="outline" onClick={onRestore} disabled={restoreDisabled} title={restoreHint} data-testid={`trash-restore-${id}`}>
      <RotateCcw className="w-4 h-4 mr-1.5" /> Restore
    </Button>
    <Button size="sm" variant="ghost" onClick={onPurge} className="text-rose-600 hover:text-rose-700 hover:bg-rose-50" data-testid={`trash-purge-${id}`}>
      <Trash2 className="w-4 h-4 mr-1.5" /> Delete forever
    </Button>
  </div>
);

const Meta = ({ item }) => (
  <div className="text-xs text-slate-500 mt-0.5">
    Deleted {dateShort(item.deleted_at)}{item.deleted_by_name ? ` by ${item.deleted_by_name}` : ""} ·{" "}
    <span className="text-amber-700 font-medium" data-testid={`trash-days-${item.id}`}>removed in {plural(daysLeft(item.purge_at), "day")}</span>
  </div>
);

export const TrashLoadRow = ({ item, onRestore, onPurge }) => (
  <div className="p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`trash-load-${item.id}`}>
    <Truck className="hidden sm:block w-5 h-5 text-slate-400 shrink-0" />
    <div className="flex-1 min-w-0">
      <div className="font-semibold">#{item.carrier_number || item.load_number}{item.carrier_number && <span className="font-normal text-xs text-slate-500"> (ref #{item.load_number})</span>} <span className="font-normal text-slate-600">· {item.broker_name} · {money(item.rate)}</span></div>
      <div className="text-xs text-slate-600 mt-0.5">
        {item.status} · {item.doc_count ? plural(item.doc_count, "document") : "no documents"}{item.invoice_number ? ` · invoice ${item.invoice_number}` : ""}
      </div>
      <Meta item={item} />
    </div>
    <Actions id={item.id} onRestore={onRestore} onPurge={onPurge} />
  </div>
);

export const TrashDocRow = ({ item, onRestore, onPurge }) => {
  const Icon = item.content_type?.startsWith("image/") ? ImgIcon : FileText;
  return (
    <div className="p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`trash-doc-${item.id}`}>
      <Icon className="hidden sm:block w-5 h-5 text-slate-400 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="font-semibold truncate">{item.original_filename}</div>
        <div className="text-xs text-slate-600 mt-0.5">
          {item.type === "POD" ? "POD/BOL" : "Ratecon"} · from load #{item.carrier_number || item.load_number || "—"}
          {item.uploaded_by_name ? ` · uploaded by ${item.uploaded_by_name}` : ""}
          {item.load_in_trash && <span className="text-rose-600"> · its load is in Trash, restore the load first</span>}
        </div>
        <Meta item={item} />
      </div>
      <Actions
        id={item.id}
        onRestore={onRestore}
        onPurge={onPurge}
        restoreDisabled={item.load_in_trash}
        restoreHint={item.load_in_trash ? "Restore the load first" : undefined}
      />
    </div>
  );
};
